import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import '../sic-icon/sic-icon';
import componentStyles from './sic-filter-container.css.lit';
import { trapFocus } from '../../../utilities/trap-focus';
/**
 * The Sic Filter Container component
 * @element sic-filter-container
 * @extends OutlineElement
 *
 * @state {Boolean} isMobile - Wether the screen is mobile or not.
 * @state {Boolean} isActive - Wether the panel is active/open.
 *
 * @slot filter-label: label for reveal button on mobile sizes.
 * @slot filters: filter content.
 *
 */

@customElement('sic-filter-container')
export class SicFilterContainer extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @state()
  isMobile = true;

  @state()
  isActive = false;

  firstUpdated() {
    const maxWidthScreen = screenSizeMap.get('md') as number;
    this.mobileMode(maxWidthScreen);
    this.requestUpdate();
    this.toggleActive();
    this.filterSkipToListener();
  }

  toggleActive() {
    if (this.isMobile) {
      this.requestUpdate();
      this.isActive = false;
      setTimeout(() => {
        const details = this.shadowRoot?.querySelector(
          'details.filter-container'
        ) as HTMLDetailsElement;
        details?.addEventListener('toggle', () => {
          if (details.open) {
            this.isActive = true;
            trapFocus([this.shadowRoot, this]);
          } else {
            this.isActive = false;
            // TODO figure out how to remove listener in trapFocus
          }
        });
      }, 500);
    }
  }
  filterSkipToListener() {
    if (this.isMobile) {
      setTimeout(() => {
        const searchFilterLink = document?.querySelector(
          'a#skip-to-filters'
        ) as HTMLAnchorElement;
        const summary = this.shadowRoot?.querySelector(
          'summary.filter-container-label'
        ) as HTMLDetailsElement;

        if (searchFilterLink != null) {
          searchFilterLink?.addEventListener('click', e => {
            if (!this.isActive) {
              e.preventDefault();
              summary.click();
            }
          });
        }
      }, 500);
    }
  }

  mobileMode(width: number) {
    const maxMobileWidth = width - 1;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);
    // Set initial value to isMobile
    this.isMobile = mediaQueryList.matches;

    // update isMobile value only when screen width is crossing maxMobileWidth (1024)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
      this.toggleActive();
      this.filterSkipToListener();
    });
  }

  render(): TemplateResult {
    const isMobile = this.isMobile ? 'mobile' : null;

    if (this.isMobile) {
      return html` <details class="filter-container">
        <summary class="filter-container-label ${isMobile}">
          ${this.setIcon()}
        </summary>
        <div class="filter-content">
          <slot name="filters" class="filters"></slot>
        </div>
      </details>`;
    } else {
      return html`<slot name="filters" class="filters"></slot>`;
    }
  }

  setIcon() {
    if (this.isActive) {
      return html`<span class="filter-label-close" role="button">Close</span
        ><sic-icon
          name="times"
          size="24px"
          svg-fill="currentColor"
        ></sic-icon>`;
    } else {
      return html`
        <slot name="filter-label" class="filter-label"></slot>
        <sic-icon name="filter" size="24px" svg-fill="currentColor"></sic-icon>
      `;
    }
  }
}
